import { Controller } from "@hotwired/stimulus"

// Busqueda live que usa la URL como unica fuente de verdad.
//
// Los filtros (links dentro del turbo-frame) escriben su estado a la URL.
// El input de busqueda vive FUERA del frame (conserva foco) y, al tipear,
// mergea search=<valor> sobre la URL actual (que ya tiene los filtros activos)
// y recarga SOLO el frame via frame.src. Asi nunca se pierden filtros ni texto,
// y no hay params "horneados" stale en ninguna direccion.
//
// Mientras el frame esta cargando, muestra un spinner a la derecha del input.
//
// Uso:
//   <input data-controller="manage--list-search"
//          data-manage--list-search-frame-value="contacts_list"
//          data-action="input->manage--list-search#search">
export default class extends Controller {
  static values = {
    frame: String,
    param: { type: String, default: "search" },
    delay: { type: Number, default: 300 }
  }

  connect() {
    this.frame = document.getElementById(this.frameValue)
    this.spinner = this.#buildSpinner()
    this.element.parentNode.appendChild(this.spinner)

    this._onLoad = () => this.#toggleSpinner(false)
    this.frame?.addEventListener("turbo:frame-render", this._onLoad)
  }

  disconnect() {
    clearTimeout(this._timeout)
    this.frame?.removeEventListener("turbo:frame-render", this._onLoad)
    this.spinner?.remove()
  }

  search() {
    clearTimeout(this._timeout)
    this._timeout = setTimeout(() => this.#navigate(), this.delayValue)
  }

  #navigate() {
    if (!this.frame) return

    const url = new URL(window.location.href)
    const value = this.element.value.trim()

    if (value) {
      url.searchParams.set(this.paramValue, value)
    } else {
      url.searchParams.delete(this.paramValue)
    }
    url.searchParams.delete("p")

    // Si el src no cambia, Turbo no recarga (no dispara turbo:frame-load) y el
    // spinner quedaria colgado. Solo navegamos cuando hay un cambio real.
    const nextSrc = url.pathname + url.search
    if (this.frame.getAttribute("src") === nextSrc) return

    this.#toggleSpinner(true)
    this.frame.src = nextSrc
  }

  #buildSpinner() {
    // Spinner CSS puro (mismo estilo que manage/common/_spinner). NO usar un
    // <i> de FontAwesome: esta en modo SVG+JS y no convierte iconos agregados
    // dinamicamente, quedando invisible. Posicion por estilo inline para no
    // depender de clases arbitrarias de Tailwind escritas en JS.
    const el = document.createElement("div")
    el.className = "border-2 border-gray-300 border-t-black rounded-full"
    Object.assign(el.style, {
      position: "absolute",
      right: "12px",
      top: "50%",
      width: "16px",
      height: "16px",
      display: "none"
    })
    // Rotacion via Web Animations API (no depende de la clase animate-spin de
    // Tailwind, que puede no estar en el bundle CSS de esta pagina).
    el.animate(
      [
        { transform: "translateY(-50%) rotate(0deg)" },
        { transform: "translateY(-50%) rotate(360deg)" }
      ],
      { duration: 800, iterations: Infinity }
    )
    return el
  }

  #toggleSpinner(loading) {
    if (this.spinner) this.spinner.style.display = loading ? "block" : "none"
  }
}
